/**
 * VoiceConversation Helpers
 * Single module for all voice-conversation utilities:
 * - Chief complaint extraction and CMR demographics (from former voiceConversationHelpers.js)
 * - Response parsing, transcript formatting, question matching, proxy detection
 */

import { calculateAge } from '../../../../chartmind/components/chartmind/services/cmrReadService';
import { 
  WORD_NUMBERS, 
  FILLER_WORDS, 
  INCOMPLETE_PATTERNS,
  OPENING_PROMPT,
  CLOSING_PROMPT,
  CLOSING_PROMPT_MAX_REACHED,
} from './constants';

// =============================================================================
// CHIEF COMPLAINT EXTRACTION
// =============================================================================

/** Incomplete/fragment phrases that must never be used as chief complaint (e.g. "been having", "having") */
const INVALID_CHIEF_COMPLAINT_FRAGMENTS = [
  'been having', 'been experiencing', 'having', 'experiencing', 'feeling', 'got',
  'something', 'something wrong', 'some things', 'this thing', 'some stuff',
  'a few things', 'issues', 'problems', 'things', 'stuff', 'something going on',
  'been', 'having something', 'experiencing something', 'feeling something',
];

/** Known symptom words used to detect if the user named a problem (not just onset/timing) */
const KNOWN_SYMPTOM_WORDS = [
  'headache', 'head ache', 'migraine', 'chest pain', 'chest tightness', 'chest discomfort',
  'stomach pain', 'stomach ache', 'abdominal pain', 'belly pain', 'back pain', 'lower back pain',
  'upper back pain', 'neck pain', 'stiff neck', 'sore throat', 'throat pain', 'cough', 'coughing',
  'fever', 'chills', 'sweats', 'nausea', 'vomiting', 'throwing up', 'diarrhea', 'constipation',
  'dizziness', 'dizzy', 'lightheaded', 'vertigo', 'fatigue', 'tired', 'exhausted', 'weakness',
  'shortness of breath', 'trouble breathing', 'difficulty breathing', "can't breathe",
  'rash', 'skin rash', 'hives', 'itching', 'itchy', 'joint pain', 'muscle pain', 'body aches',
  'ear pain', 'earache', 'eye pain', 'vision problems', 'blurry vision', 'numbness', 'tingling',
  'swelling', 'swollen', 'bleeding', 'blood', 'pain', 'ache', 'aches', 'discomfort', 'burning', 'pressure',
];

/** Phrases that describe onset/timing/duration/severity without naming what "it" is */
const ONSET_TEMPORAL_PATTERNS = [
  /\bit\s+started\b/i,
  /\bit\s+came\s+on\b/i,
  /\bit\s+began\b/i,
  /\bstarted\s+gradually\b/i,
  /\bstarted\s+suddenly\b/i,
  /\bcame\s+on\s+gradually\b/i,
  /\bcame\s+on\s+suddenly\b/i,
  /\bgradually\b/i,
  /\bsuddenly\b/i,
  /\bslowly\s+over\b/i,
  /\bit('s|s)\s+been\s+(?:like\s+)?this\b/i,
  /\bfor\s+(?:about\s+)?(?:a\s+)?(?:few\s+)?(?:days?|weeks?|months?|years?)\b/i,
  /\b(?:for\s+)?(?:half|a\s+couple|several)\s+(?:days?|weeks?|months?|years?)\b/i,
  /\bover\s+the\s+(?:past|last)\s+/i,
  /\bit\s+gets?\s+(?:better|worse)\b/i,
  /\bit\s+comes?\s+and\s+goes?\b/i,
  /\b(?:scale|severity|1\s+to\s+10)\b/i,
  /\b(?:how\s+long|duration)\b/i,
  /\b(?:makes?\s+it\s+better|makes?\s+it\s+worse)\b/i,
  /\blocation\s+of\s+(?:the\s+)?pain\b/i,
];

/**
 * Returns true if the extracted string is an incomplete fragment, not a real symptom.
 */
export function isInvalidChiefComplaintFragment(complaint) {
  if (!complaint || typeof complaint !== 'string') return true;
  const lower = complaint.toLowerCase().trim();
  if (lower.length < 4) return true;
  if (INVALID_CHIEF_COMPLAINT_FRAGMENTS.some(frag => lower === frag || lower.startsWith(frag + ' ') || lower.endsWith(' ' + frag))) return true;
  if (/^(just|only|really|very|quite|pretty)\s+/i.test(lower) && lower.length < 20) return true;
  return false;
}

/**
 * Returns true if the transcript describes only onset/timing/duration without naming the actual symptom.
 */
export function isOnsetOrTemporalOnly(transcript) {
  if (!transcript || typeof transcript !== 'string') return false;
  const text = transcript.toLowerCase().trim();
  if (text.length < 5) return false;
  if (KNOWN_SYMPTOM_WORDS.some(s => text.includes(s))) return false;
  if (/\bmy\s+(head|back|chest|stomach|throat|neck|ear|eye)s?\b/i.test(text)) return false;
  const hasOnsetTemporal = ONSET_TEMPORAL_PATTERNS.some(p => p.test(text));
  if (!hasOnsetTemporal) return false;
  if (/^\s*it\s+(?:started|came|began|got)\s+\w+\s*\.?\s*$/i.test(text)) return true;
  if (/^\s*(?:gradually|suddenly|slowly)(?:\s+but)?\s*\.?\s*$/i.test(text)) return true;
  return true;
}

/**
 * Extract the chief complaint from the patient's initial response.
 * Returns { complaint: string, isAmbiguous: boolean }
 */
export function extractChiefComplaint(transcript) {
  if (!transcript || transcript.length < 3) {
    return { complaint: null, isAmbiguous: true };
  }
  const text = transcript.toLowerCase().trim();
  const symptomPatterns = [
    /i(?:'ve|'m| have| got| am having| am experiencing| have been having| have been experiencing)\s+(?:a |an |some |bad |terrible |severe |mild )?(.+?)(?:\.|,|$| and | for | since | that)/i,
    /my\s+(\w+(?:\s+\w+)?)\s+(?:hurts?|aches?|is\s+\w+ing|feels?\s+\w+)/i,
    /^(.+?)\s+for\s+(?:about\s+)?(?:\d+|a|the|several)/i,
    /(?:having|experiencing|feeling|got)\s+(?:a |an |some )?(.+?)(?:\.|,|$| and | for )/i,
  ];
  for (const symptom of KNOWN_SYMPTOM_WORDS) {
    if (text.includes(symptom)) {
      return { complaint: symptom, isAmbiguous: false };
    }
  }
  for (const pattern of symptomPatterns) {
    const match = text.match(pattern);
    if (match && match[1]) {
      const extracted = match[1].trim();
      const cleaned = extracted
        .replace(/^(a |an |some |this |bad |terrible |really |very |quite |pretty )/i, '')
        .trim();
      if (cleaned.length >= 3 && cleaned.length <= 50 && !isInvalidChiefComplaintFragment(cleaned)) {
        return { complaint: cleaned, isAmbiguous: false };
      }
    }
  }
  const conjunctions = text.match(/\b(and|also|plus|as well as)\b/g);
  if (conjunctions && conjunctions.length >= 1) {
    const firstPart = text.split(/\band\b|\balso\b|\bplus\b/i)[0].trim();
    if (firstPart.length >= 5) {
      for (const symptom of KNOWN_SYMPTOM_WORDS) {
        if (firstPart.includes(symptom)) {
          return { complaint: symptom, isAmbiguous: true };
        }
      }
    }
    return { complaint: null, isAmbiguous: true };
  }
  if (isOnsetOrTemporalOnly(transcript)) {
    return { complaint: null, isAmbiguous: true };
  }
  if (text.length >= 10) {
    return { complaint: 'symptoms', isAmbiguous: false };
  }
  return { complaint: null, isAmbiguous: true };
}

// =============================================================================
// CMR DEMOGRAPHICS
// =============================================================================

/**
 * Generate a demographics confirmation question based on CMR data
 * @param {Object} cmrData - CMR data with demographics.dateOfBirth and demographics.sex
 * @returns {{ question: string, age: number|null, sex: string|null } | null}
 */
export function generateCMRDemographicsConfirmation(cmrData) {
  if (!cmrData?.demographics) {
    return null;
  }
  const { dateOfBirth, sex } = cmrData.demographics;
  const age = calculateAge(dateOfBirth);
  const normalizedSex = sex?.toLowerCase() === 'male' ? 'male'
    : sex?.toLowerCase() === 'female' ? 'female'
    : null;
  if (age === null && !normalizedSex) {
    return null;
  }
  let question = "Based on your medical records, I see you're ";
  if (age !== null && normalizedSex) {
    question += `a ${age}-year-old ${normalizedSex}`;
  } else if (age !== null) {
    question += `${age} years old`;
  } else if (normalizedSex) {
    question += normalizedSex;
  }
  question += " - is that correct?";
  return { question, age, sex: normalizedSex };
}

// =============================================================================
// RESPONSE PARSING
// =============================================================================

/**
 * Parse age from user response
 * Handles: "25", "I'm 32", "twenty-five", "32 years old", "twenty-two-year-old male", etc.
 * Prefers explicit "year(s) old" phrases and two-digit ages so STT like "2 year old" for "22-year-old"
 * does not beat a later "22" in the same string.
 * @param {string} response - User's speech response
 * @returns {number|null} - Parsed age or null if not found
 */
export function parseAge(response) {
  if (!response || typeof response !== 'string') return null;
  const text = response.toLowerCase().trim();

  // Explicit "N-year-old" / "N years old" (digits)
  const yearOldDigit = text.match(
    /\b(\d{1,3})\s*-?\s*years?\s*-?\s*old\b/
  );
  if (yearOldDigit) {
    const age = parseInt(yearOldDigit[1], 10);
    if (age >= 0 && age <= 120) return age;
  }

  // "I'm 32" / "I am 45" / "turned 30" — ignore "I am 2 weeks…" / pregnancy-style phrasing
  const imAge = text.match(
    /\b(?:i'?m|i am|turned)\s+(\d{1,3})\b(?!\s*-?\s*(?:week|month|day)s?\b)/
  );
  if (imAge) {
    const age = parseInt(imAge[1], 10);
    if (age >= 0 && age <= 120) return age;
  }

  // All standalone digit groups; prefer 10–120 over a lone 1–9 (reduces false toddlers on "22" → "2 …")
  // Skip digits that only quantify weeks/months/days (not years of age).
  const digitMatches = [...text.matchAll(/\b(\d{1,3})\b(?!\s*-?\s*(?:week|month|day)s?\b)/g)]
    .map((m) => parseInt(m[1], 10))
    .filter((n) => !Number.isNaN(n) && n >= 0 && n <= 120);
  const twoOrThreeDigit = digitMatches.find((n) => n >= 10 && n <= 120);
  if (twoOrThreeDigit !== undefined) return twoOrThreeDigit;
  if (digitMatches.length === 1) return digitMatches[0];

  // Spelled ages: "twenty-two-year-old" → twenty + two (hyphens → spaces)
  let total = 0;
  let found = false;
  const words = text.replace(/-/g, ' ').replace(/,/g, ' ').split(/\s+/).filter(Boolean);

  for (const word of words) {
    if (WORD_NUMBERS[word] !== undefined) {
      total += WORD_NUMBERS[word];
      found = true;
    }
  }

  if (found && total >= 0 && total <= 120) return total;

  return null;
}

/** Default max re-prompts after an unparseable age answer (initial ask is separate). */
export const DEFAULT_MAX_AGE_REPROMPTS = 2;

/**
 * Bounded age capture: accept parsed age, re-prompt, or fall through as unknown.
 * Shared by dashboard SC2 and /scmap voice flows — never hard-blocks the patient.
 *
 * @param {{ currentAttempts: number, maxReprompts?: number, parsedAge: number | null }} params
 * @returns {{ action: 'captured' | 'reprompt' | 'unknown', age: number | 'unknown' | null, nextAttempts: number }}
 */
export function resolveBoundedAgeCapture({
  currentAttempts,
  maxReprompts = DEFAULT_MAX_AGE_REPROMPTS,
  parsedAge,
}) {
  if (parsedAge !== null && Number.isFinite(parsedAge)) {
    return { action: "captured", age: parsedAge, nextAttempts: 0 };
  }

  const nextAttempts = currentAttempts + 1;
  if (nextAttempts > maxReprompts) {
    return { action: "unknown", age: "unknown", nextAttempts };
  }
  return { action: "reprompt", age: null, nextAttempts };
}

/**
 * Whether structured demographics age is resolved (numeric or explicit unknown).
 *
 * @param {number | string | null | undefined} patientAge
 * @returns {boolean}
 */
export function isDemographicsAgeResolved(patientAge) {
  return patientAge !== null && patientAge !== undefined;
}

/**
 * Parse sex from user response
 * Handles: "male", "female", "man", "woman", "M", "F", "I'm a guy", etc.
 * Also handles common speech-to-text errors like "mail" → "male"
 * @param {string} response - User's speech response
 * @returns {'male'|'female'|null} - Parsed sex or null if not found
 */
export function parseSex(response) {
  const text = response.toLowerCase().trim();
  
  // Check for female indicators (avoid bare \bf\b — it false-matches inside contractions)
  if (
    /\b(female|woman|girl)\b/.test(text) ||
    /(?:^|[\s,(])f(?:[\s,.)]|$)/.test(text) ||
    /i'?m a (woman|girl|lady|female)/.test(text) ||
    /i am a (woman|girl|lady|female)/.test(text)
  ) {
    return 'female';
  }

  // Check for male indicators
  // IMPORTANT: "mail" is a common speech-to-text error for "male"
  // Do not use bare \bm\b — it false-matches the "m" in "I'm".
  if (
    /\b(male|mail|man|guy|boy|dude|gentleman)\b/.test(text) ||
    /(?:^|[\s,(])m(?:[\s,.)]|$)/.test(text) ||
    /i'?m a (man|guy|male|mail|boy|dude|gentleman)/.test(text) ||
    /i am a (man|guy|male|mail|boy|dude|gentleman)/.test(text)
  ) {
    return 'male';
  }
  
  return null;
}

/**
 * Parse pregnancy status from user response
 * @param {string} response - User's speech response
 * @returns {'yes'|'no'|'unknown'|null} - Parsed status or null if unclear
 */
export function parsePregnancy(response) {
  const text = response.toLowerCase().trim();
  
  // Positive indicators
  if (/\b(yes|yeah|yep|possibly|maybe|might be|could be|i think so|pregnant)\b/.test(text)) {
    // Check for negation
    if (/\b(no|not|don'?t|can'?t|impossible|definitely not)\b/.test(text)) {
      return 'no';
    }
    return 'yes';
  }
  
  // Negative indicators
  if (/\b(no|nope|not|don'?t think|can'?t be|impossible|definitely not|absolutely not)\b/.test(text)) {
    return 'no';
  }
  
  // Unknown
  if (/\b(don'?t know|unsure|not sure|uncertain|i don'?t know)\b/.test(text)) {
    return 'unknown';
  }
  
  return null;
}

/**
 * Parse fever/chills status from user response
 * @param {string} response - User's speech response
 * @returns {'yes'|'no'|'unknown'|null} - Parsed status or null if unclear
 */
export function parseFeverChills(response) {
  const text = response.toLowerCase().trim();
  
  // Positive indicators
  if (/\b(yes|yeah|yep|fever|chills|hot|cold|shivering|shivers|sweating|sweats|temperature|high temp|febrile)\b/.test(text)) {
    // Check for negation
    if (/\b(no|not|don'?t|never|none|haven'?t|did not|didnt|without)\b/.test(text)) {
      return 'no';
    }
    return 'yes';
  }
  
  // Negative indicators
  if (/\b(no|nope|not|none|never|don'?t think|haven'?t had|didn'?t have|no fever|no chills)\b/.test(text)) {
    return 'no';
  }
  
  // Unknown
  if (/\b(don'?t know|unsure|not sure|uncertain|i don'?t know)\b/.test(text)) {
    return 'unknown';
  }
  
  return null;
}

// =============================================================================
// INCOMPLETE SENTENCE DETECTION
// =============================================================================

/**
 * Check if speech appears to be an incomplete sentence
 * If the user is mid-thought, we should wait longer before proceeding
 * @param {string} speech - User's speech
 * @returns {boolean} - True if sentence appears incomplete
 */
export function isIncompleteSentence(speech) {
  if (!speech || speech.trim().length === 0) return false;
  
  const trimmed = speech.trim().toLowerCase();
  
  for (const pattern of INCOMPLETE_PATTERNS) {
    if (pattern.test(trimmed)) {
      return true;
    }
  }
  
  // Also check: very short (< 3 words) and no ending punctuation
  const words = trimmed.split(/\s+/);
  const hasEndPunctuation = /[.!?]$/.test(trimmed);
  if (words.length < 3 && !hasEndPunctuation) {
    return true;
  }
  
  return false;
}

// =============================================================================
// FUZZY QUESTION MATCHING
// =============================================================================

/**
 * Coerce question to string; LLM/clarifications can be string or { text } or { question }.
 * @param {string|Object|*} question - Question text or object with .text / .question
 * @returns {string}
 */
function toQuestionString(question) {
  if (question == null) return '';
  if (typeof question === 'string') return question;
  if (typeof question?.text === 'string') return question.text;
  if (typeof question?.question === 'string') return question.question;
  return '';
}

/**
 * Normalize a question for comparison - removes punctuation, lowercase, etc.
 * @param {string|Object|*} question - Question text (or object with .text / .question)
 * @returns {string} - Normalized question
 */
export function normalizeQuestion(question) {
  const str = toQuestionString(question);
  return str
    .toLowerCase()
    .replace(/[?.,!'"]/g, '')     // Remove punctuation
    .replace(/\s+/g, ' ')         // Collapse whitespace
    .trim();
}

/**
 * Extract key topic words from a question (removing common filler words)
 * @param {string} question - Question text
 * @returns {string[]} - Array of topic words
 */
export function extractTopicWords(question) {
  const normalized = normalizeQuestion(question);
  const words = normalized.split(' ');
  return words.filter(w => w.length > 2 && !FILLER_WORDS.has(w));
}

/**
 * Check if two questions are semantically similar
 * Returns true if they share enough topic words to be considered duplicates
 * @param {string} q1 - First question
 * @param {string} q2 - Second question
 * @param {number} threshold - Similarity threshold (0-1)
 * @returns {boolean} - True if questions are similar
 */
export function areQuestionsSimilar(q1, q2, threshold = 0.4) {
  const words1 = extractTopicWords(q1);
  const words2 = extractTopicWords(q2);
  
  if (words1.length === 0 || words2.length === 0) return false;
  
  // Count overlapping words
  const set1 = new Set(words1);
  const overlap = words2.filter(w => set1.has(w)).length;
  
  // Calculate similarity as percentage of smaller set
  const minLength = Math.min(words1.length, words2.length);
  const similarity = overlap / minLength;
  
  return similarity >= threshold;
}

/**
 * Check if a question is similar to any in a list of already-asked questions
 * @param {string} newQuestion - New question to check
 * @param {string[]} askedQuestions - List of already-asked questions
 * @param {number} [threshold] - Optional similarity threshold override
 * @returns {boolean} - True if question was already asked
 */
export function isQuestionAlreadyAsked(newQuestion, askedQuestions, threshold) {
  if (askedQuestions.includes(newQuestion)) return true;
  
  for (const asked of askedQuestions) {
    if (areQuestionsSimilar(newQuestion, asked, threshold)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Assistant-only asked list for canonical prerequisite checks.
 * User free-text must not count as "already asked" (e.g. "I'm having back pain"
 * must not skip "How long have you been experiencing the back pain?").
 *
 * @param {string[]} questionsAsked
 * @param {Array<{ speaker?: string, text?: string }>} conversationLog
 * @returns {string[]}
 */
export function getAssistantAskedQuestions(questionsAsked, conversationLog) {
  const fromLog = (Array.isArray(conversationLog) ? conversationLog : [])
    .filter((entry) => entry?.speaker === 'assistant' && entry?.text)
    .map((entry) => String(entry.text));
  return [...(Array.isArray(questionsAsked) ? questionsAsked : []), ...fromLog];
}

/**
 * Short negative-only reply to a demographics prompt (e.g. "No.", "Nope").
 * @param {string} text
 * @returns {boolean}
 */
export function isNegativeOnlyDemographicsResponse(text) {
  const normalized = String(text || '').trim().toLowerCase();
  if (!normalized || normalized.length > 24) return false;
  return /^(no|nope|nah|none|not really|don't know|dont know|prefer not|rather not)[\s.!?]*$/i.test(
    normalized
  );
}

/**
 * Check whether a question's topic words are already covered by free-text corpus.
 * Useful for skipping factor questions that the user already answered naturally.
 * @param {string} question - Candidate question
 * @param {string} corpusText - Combined transcript/user responses
 * @param {number} threshold - Similarity threshold (0-1)
 * @returns {boolean}
 */
export function isQuestionCoveredByCorpus(question, corpusText, threshold = 0.6) {
  const questionWords = extractTopicWords(question);
  if (!questionWords.length) return false;

  const corpusWords = extractTopicWords(String(corpusText || ''));
  if (!corpusWords.length) return false;

  const corpusSet = new Set(corpusWords);
  const overlap = questionWords.filter((word) => corpusSet.has(word)).length;
  const similarity = overlap / questionWords.length;
  return similarity >= threshold;
}

/**
 * Check if a question is the opening statement (should only be spoken once)
 * @param {string|Object|*} question - Question text (or object with .text / .question)
 * @returns {boolean} - True if this is an opening statement
 */
export function isOpeningStatement(question) {
  const str = toQuestionString(question);
  if (!str) return false;
  const lower = str.toLowerCase();
  return lower.includes("hello, i'm here to help") ||
         lower.includes('welcome to the symptom checker') ||
         lower.includes('i am the voice of medical superintelligence') ||
         lower.includes('cannot provide a diagnosis, but i can help you consider') ||
         lower.includes('what is the main problem that you') ||
         lower.includes("what's the main problem") ||
         lower.includes('main problem you are experiencing') ||
         lower.includes("main problem you're experiencing");
}

// =============================================================================
// CLEAN INCREMENTAL STT REPETITION
// =============================================================================

/**
 * Clean incremental repetition from STT (e.g. "I I'm I'm having I'm having back I'm having back pain" -> "I'm having back pain").
 * STT often sends growing finals that get concatenated; we want the longest suffix with no repeated consecutive bigram.
 * @param {string} text - Raw transcript that may contain repeated growing phrases
 * @returns {string} - Cleaned text
 */
export function cleanIncrementalRepetition(text) {
  if (!text || typeof text !== 'string') return text;
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 2) return text.trim();
  let start = 0;
  for (let i = 1; i < words.length; i++) {
    for (let j = 0; j < i; j++) {
      if (words[j] === words[i] && words[j + 1] === words[i + 1]) {
        start = Math.max(start, i);
        break;
      }
    }
  }
  return words.slice(start).join(' ');
}

// =============================================================================
// TRANSCRIPT FORMATTING
// =============================================================================

/**
 * Formats conversation log into structured format for LLM analysis
 * Chronological format that captures the full conversation flow
 * @param {Array} conversationLog - Array of {speaker, text, timestamp} entries
 * @param {string} initialSymptoms - Any symptoms typed before voice mode
 * @returns {string} - Formatted transcript
 */
export function formatStructuredTranscript(conversationLog, initialSymptoms = '') {
  let transcript = '';
  
  // Initial symptoms (if any typed before voice)
  if (initialSymptoms.trim()) {
    transcript += `INITIAL SYMPTOMS:\n${initialSymptoms.trim()}\n\n`;
  }
  
  // Full conversation in chronological order
  // Filter out opening/closing prompts
  const conversationEntries = conversationLog.filter(
    entry => entry.text !== OPENING_PROMPT &&
             entry.text !== CLOSING_PROMPT &&
             entry.text !== CLOSING_PROMPT_MAX_REACHED
  );
  
  if (conversationEntries.length > 0) {
    transcript += `CONVERSATION TRANSCRIPT:\n`;
    
    for (const entry of conversationEntries) {
      if (entry.speaker === 'user') {
        transcript += `[User]: ${entry.text.trim()}\n`;
      } else if (entry.speaker === 'assistant') {
        transcript += `[System]: ${entry.text.trim()}\n`;
      }
    }
  }
  
  return transcript.trim();
}

/**
 * Formats conversation for display in the UI (more readable)
 * @param {Array} conversationLog - Array of {speaker, text, timestamp} entries
 * @param {string} initialSymptoms - Any symptoms typed before voice mode
 * @returns {string} - Formatted display transcript
 */
export function formatDisplayTranscript(conversationLog, initialSymptoms = '') {
  let display = '';
  
  // Initial symptoms
  if (initialSymptoms.trim()) {
    display += `📝 Initial description:\n${initialSymptoms.trim()}\n\n`;
  }
  
  // Show all log entries so transcript matches TTS (no filtering of opening/closing)
  if (conversationLog.length > 0) {
    display += `💬 Conversation:\n`;
    for (const entry of conversationLog) {
      if (entry.speaker === 'user') {
        display += `  User: "${entry.text.trim()}"\n`;
      } else {
        display += `  System: "${entry.text.trim()}"\n`;
      }
    }
  }
  
  return display.trim();
}

/**
 * Get the last user response from conversation log
 * @param {Array} conversationLog - Array of conversation entries
 * @returns {string} - Last user response or empty string
 */
export function getLastUserResponse(conversationLog) {
  for (let i = conversationLog.length - 1; i >= 0; i--) {
    if (conversationLog[i].speaker === 'user') {
      return conversationLog[i].text;
    }
  }
  return '';
}

// =============================================================================
// PROXY MODE HELPERS - For parents/guardians describing symptoms for minors
// =============================================================================

/**
 * True when the transcript is much more likely **family history** (a relative's
 * past conditions) than "I am the proxy reporter for another symptomatic patient."
 * Used to avoid proxy confirmation when phrases like "my father" appear in FH context.
 *
 * @param {string} text
 * @returns {boolean}
 */
export function shouldSuppressProxyForFamilyHistory(text) {
  if (!text || typeof text !== 'string') return false;
  const t = text.toLowerCase().replace(/\s+/g, ' ');

  if (/\bfamily history\b/.test(t)) return true;
  if (/\b(genetic predisposition|runs in (my |the )?family)\b/.test(t)) return true;

  const rel =
    '(?:father|mother|dad|mom|mum|parent|grandfather|grandmother|grandma|grandpa|granddad|husband|wife|partner|spouse|brother|sister|sibling|uncle|aunt|cousin)';

  if (
    new RegExp(
      `\\bmy\\s+${rel}\\s+(?:had|has had|was diagnosed|received a diagnosis|suffered from|died from|died of|passed away from)\\b`,
      'i'
    ).test(t)
  ) {
    return true;
  }

  if (new RegExp(`\\bmy\\s+${rel}\\b[^.]{0,120}\\bat age\\s+\\d+`, 'i').test(t)) {
    return true;
  }
  if (new RegExp(`\\bmy\\s+${rel}\\b[^.]{0,120}\\bage\\s+\\d+\\s+when\\b`, 'i').test(t)) {
    return true;
  }

  return false;
}

/**
 * Heuristic: LLM/voice clarifier asking whether symptoms are the user's vs someone else's.
 * @param {string} question
 * @returns {boolean}
 */
export function questionSoundsLikeProxyDisambiguation(question) {
  if (!question || typeof question !== 'string') return false;
  const q = question.toLowerCase();
  if (/\bparent or guardian\b/.test(q)) return true;
  if (/\b(the person you're describing|the person you are describing)\b/.test(q)) return true;
  if (/\b(describ|symptom|concerned about|talking about|speaking about)\b/.test(q)) {
    if (/\b(yourself|someone else|another person|for you|for them)\b/.test(q)) return true;
  }
  if (/\bwhose symptoms\b/.test(q)) return true;
  if (/\b(on whose behalf|who (is|are) (the |this )?patient)\b/.test(q)) return true;
  return false;
}

/**
 * @param {string} transcript
 * @param {string[]} questions
 * @returns {string[]}
 */
export function filterProxyDisambiguationClarifyingQuestions(transcript, questions) {
  if (!shouldSuppressProxyForFamilyHistory(transcript) || !Array.isArray(questions)) {
    return Array.isArray(questions) ? questions : [];
  }
  return questions.filter((q) => !questionSoundsLikeProxyDisambiguation(String(q || '')));
}

/**
 * Detect if chief complaint indicates proxy situation (speaking for someone else)
 * @param {string} text - Chief complaint text
 * @returns {{ isProxy: boolean, relationship: string|null, subjectAge: 'infant'|'child'|'adult'|null }}
 */
export function detectProxyIndicators(text) {
  if (!text) return { isProxy: false, relationship: null, subjectAge: null };
  
  const lowerText = text.toLowerCase();
  const suppressAdultFamilyProxy = shouldSuppressProxyForFamilyHistory(text);
  
  // Check for infant/baby indicators (likely under 1 year)
  // Includes specific age patterns like "6 week old", "3 month old"
  if (/\b(my\s+)?(baby|newborn|infant)\b/.test(lowerText) ||
      /\b\d+\s*(week|month)\s*old\b/.test(lowerText) ||
      /\b(six|seven|eight|nine|ten|eleven|twelve)\s*week\s*old\b/.test(lowerText)) {
    return { isProxy: true, relationship: 'parent', subjectAge: 'infant' };
  }
  
  // Check for child indicators (general pediatric)
  if (/\b(my\s+)?(child|son|daughter|kid|toddler|teenager|teen)\b/.test(lowerText)) {
    return { isProxy: true, relationship: 'parent', subjectAge: 'child' };
  }
  
  // Check for adult family member indicators
  if (
    !suppressAdultFamilyProxy &&
    /\b(my\s+)?(husband|wife|mother|father|mom|dad|grandmother|grandfather|grandma|grandpa|partner|spouse|parent)\b/.test(lowerText)
  ) {
    return { isProxy: true, relationship: 'family', subjectAge: 'adult' };
  }
  
  // Check for generic "someone else" patterns
  if (/\b(not (me|myself)|someone else|another person|my friend)\b/.test(lowerText)) {
    return { isProxy: true, relationship: 'other', subjectAge: null };
  }
  
  return { isProxy: false, relationship: null, subjectAge: null };
}

/**
 * Parse proxy confirmation response
 * Determines if user confirmed they are a parent/guardian or if they are the patient
 * @param {string} response - User's response to proxy question
 * @returns {'parent'|'self'|'unclear'}
 */
export function parseProxyResponse(response) {
  if (!response) return 'unclear';
  
  const text = response.toLowerCase().replace(/\s+/g, ' ').trim();
  
  // Indicators they ARE a parent/guardian speaking for someone
  // Check for affirmative responses first
  if (/\b(yes|yeah|yep|correct|right|that'?s right|affirmative|oui|ouais)\b/.test(text)) {
    return 'parent';
  }

  // Negation of parent role before positive parent matches ("I'm not a parent")
  if (
    /\b((?:i'?m|i am) not (?:a |the )?(?:parent|guardian)|not speaking for|je ne suis pas (?:le |la |un |une )?(?:parent|tuteur|tutrice))\b/.test(
      text
    )
  ) {
    return 'self';
  }
  
  // Explicit parent/guardian self-identification ("I'm" / "I am" / bare role / FR)
  if (
    /\b(?:i'?m|i am)\s+(?:the |a |their )?(?:parent|guardian|mother|father|mom|dad)\b/.test(text) ||
    /\b(?:je suis)\s+(?:le |la |un |une |leur )?(?:parent|tuteur|tutrice|m[eè]re|p[eè]re|maman|papa)\b/.test(
      text
    ) ||
    /\b(speaking for|on behalf of|for (?:my|the|a) (?:baby|child|son|daughter|kid)|pour (?:mon|ma|un|une|le|la) (?:b[eé]b[eé]|enfant|fils|fille))\b/.test(
      text
    ) ||
    /^(?:the |a |their )?(?:parent|guardian|mother|father|mom|dad|tuteur|tutrice|m[eè]re|p[eè]re|maman|papa)\.?$/.test(
      text
    )
  ) {
    return 'parent';
  }

  // Clarifier answers: "someone else" / "a child" / "family member" (EN + FR)
  if (
    /\b(someone else|another person|family member|un enfant|quelqu'?un d'?autre)\b/.test(text) ||
    /\b(?:for|about)\s+(?:a |my |the )?(?:child|kid|baby|son|daughter|enfant)\b/.test(text) ||
    /\bpour\s+(?:un |mon |ma |le |la )?(?:enfant|fils|fille|b[eé]b[eé])\b/.test(text) ||
    /^(?:a |the )?(?:child|kid|baby|enfant|fils|fille)\.?$/.test(text)
  ) {
    return 'parent';
  }
  
  // Indicators they ARE the patient themselves (minor using system directly)
  if (
    /\b(no|non|i'?m the patient|i am the patient|it'?s (me|for me|about me)|my (own )?symptoms|i have|i'?m (sick|not feeling well|the one)|for myself|myself|pour moi(?:-m[eê]me)?|mes (?:propres )?sympt[oô]mes)\b/.test(
      text
    )
  ) {
    return 'self';
  }
  
  return 'unclear';
}

/**
 * Check if text contains correction phrases indicating it's about someone else
 * Useful for catching "It's not about me" mid-conversation
 * @param {string} text - User response text
 * @returns {boolean}
 */
export function containsProxyCorrection(text) {
  if (!text) return false;
  
  const lowerText = text.toLowerCase();
  
  return /\b(not (about|for) me|it'?s not me|i'?m not the (patient|one)|for (my|someone else))\b/.test(lowerText);
}
